import { memo, useCallback, useMemo, ChangeEvent } from 'react';
import classNames from 'classnames';
import { V2QsRangeProps, defaultV2QsRangeProps } from './types';

/**
 * V2QsRange 컴포넌트
 *
 * 슬라이더 (Range) 입력 컴포넌트
 * V1 QsInputRange의 V2 버전으로 순수 UI만 담당
 *
 * 주요 기능:
 * - 슬라이더로 값 선택
 * - min, max, step 설정
 * - 틱 마크 표시
 * - 특정 값에 스케일 라벨 표시
 */
function V2QsRange(props: V2QsRangeProps) {
  const {
    value,
    onChange,
    min = defaultV2QsRangeProps.min!,
    max = defaultV2QsRangeProps.max!,
    step = defaultV2QsRangeProps.step!,
    showTicks = defaultV2QsRangeProps.showTicks,
    showValue = defaultV2QsRangeProps.showValue,
    disabled = defaultV2QsRangeProps.disabled,
    readOnly = defaultV2QsRangeProps.readOnly,
    hasError = defaultV2QsRangeProps.hasError,
    errorMessage,
    labels,
    className,
    sliderClassName,
  } = props;

  // 틱 마크 계산 (min, max, step이 문자열로 올 수 있으므로 Number로 변환)
  const ticks = useMemo(() => {
    const minNum = Number(min);
    const maxNum = Number(max);
    const stepNum = Number(step);
    const tickCount = Math.floor((maxNum - minNum) / stepNum) + 1;
    return Array.from({ length: tickCount }, (_, idx) => minNum + stepNum * idx);
  }, [min, max, step]);

  // 값이 없을 때 기본값
  const displayValue = value ?? min;

  // 슬라이더 값 변경
  const handleChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      if (disabled || readOnly) return;
      const newValue = Number(e.target.value);
      onChange?.(newValue);
    },
    [onChange, disabled, readOnly]
  );

  // 값이 설정되지 않은 상태 (투명도 적용)
  const isValueUnset = value === undefined || value === null;

  return (
    <div className={classNames('flex flex-col', className)}>
      {/* 현재 값 표시 */}
      {showValue && (
        <div className="text-center mb-2">
          <span className="text-lg font-semibold">{displayValue}</span>
        </div>
      )}

      {/* 슬라이더 */}
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={displayValue}
        onChange={handleChange}
        disabled={disabled}
        className={classNames(
          'range range-primary rounded-full w-full',
          isValueUnset && 'opacity-40',
          hasError && 'range-error',
          sliderClassName
        )}
      />

      {/* 틱 마크 */}
      {showTicks && (
        <div className="w-full flex justify-between text-xs mt-1">
          {ticks.map((tick, idx) => (
            <span key={idx} className="w-5 text-center">
              {tick}
            </span>
          ))}
        </div>
      )}

      {/* 스케일 라벨 */}
      {labels && labels.length > 0 && (
        <div className="w-full flex justify-between text-xs mt-1">
          {ticks.map((tick) => {
            const label = labels.find((l) => l.value === tick);
            return (
              <span key={tick} className="w-5 text-center text-gray-500 leading-tight" style={{ fontSize: '0.65rem' }}>
                {label?.text || ''}
              </span>
            );
          })}
        </div>
      )}

      {/* 에러 메시지 */}
      {hasError && errorMessage && (
        <p className="text-error text-sm mt-1">{errorMessage}</p>
      )}
    </div>
  );
}

export default memo(V2QsRange);
